// =================================================================
// QuickTaskBar — Ctrl+K command-palette for quick task creation
// Path: src/components/tasks/QuickTaskBar.jsx
//
// Centered overlay with a single smart input that uses
// parseTaskDirective to extract date, priority, category, and donor
// from plain language. Shows a live preview strip. Enter to create,
// Escape to dismiss.
// =================================================================

import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Command,
  Calendar,
  AlertCircle,
  Tag,
  User,
  ArrowRight,
  Compass,
  HelpCircle,
  Flower2,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { parseTaskDirective } from "../../utils/parseTaskDirective";
import { parseNavigation, NAV_COMMAND_LIST } from "../../utils/parseNavigation";
import { POLLEN_CARD_REGISTRY } from "../dashboard/pollen/PollenGrid";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { TASK_PRIORITIES, TASK_CATEGORIES } from "../../constants/taskTypes";

const getPriorityLabel = (id) =>
  TASK_PRIORITIES.find((p) => p.id === id) || TASK_PRIORITIES[1];
const getCategoryLabel = (id) =>
  TASK_CATEGORIES.find((c) => c.id === id) || TASK_CATEGORIES[7];

export const QuickTaskBar = ({ isOpen, onClose }) => {
  const {
    palette,
    members,
    addTask,
    userProfile,
    navigate,
    setSearch,
    setShowTaskDrawer,
    setLastReportTab,
    setActivePollenCardKey,
    currentView,
    selectedMember,
    triggerLogContact,
    showMessage,
  } = useApp();

  const [input, setInput] = useState("");
  const [pollenMenuIndex, setPollenMenuIndex] = useState(0);
  const inputRef = useRef(null);

  const currentDirectorName = userProfile?.full_name || "";

  // Focus on open
  useEffect(() => {
    if (isOpen) {
      setInput("");
      setPollenMenuIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Reset submenu highlight whenever the input changes
  useEffect(() => {
    setPollenMenuIndex(0);
  }, [input]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Live parse — navigation first, then task creation as fallback
  const nav = useMemo(() => {
    if (!input.trim()) return null;
    return parseNavigation(input);
  }, [input]);

  const parsed = useMemo(() => {
    if (!input.trim() || nav) return null;
    return parseTaskDirective(input, members);
  }, [input, members, nav]);

  const hasExtras =
    parsed &&
    (parsed.dueDate ||
      parsed.priority !== "normal" ||
      parsed.category !== "general" ||
      parsed.memberId);

  const handleSubmit = async () => {
    if (!parsed || !parsed.title) return;
    await addTask({
      title: parsed.title,
      category: parsed.category,
      priority: parsed.priority,
      dueDate: parsed.dueDate,
      memberId: parsed.memberId,
      directorName: currentDirectorName,
    });
    setInput("");
    onClose();
  };

  // ── Navigation dispatch ───────────────────────────────────
  const activatePollenCard = (cardKey) => {
    navigate("main");
    if (setActivePollenCardKey) setActivePollenCardKey(cardKey);
    setInput("");
    onClose();
  };

  const handleNavigate = () => {
    if (!nav) return;

    if (nav.type === "nav") {
      // Set the report tab first if the command targets a specific tab
      if (nav.tab && nav.view === "reports" && setLastReportTab) {
        setLastReportTab(nav.tab);
      }
      navigate(nav.view);
      setInput("");
      onClose();
      return;
    }

    if (nav.type === "search") {
      setSearch(nav.search);
      navigate("donors");
      setInput("");
      onClose();
      return;
    }

    if (nav.type === "drawer") {
      setShowTaskDrawer(true);
      setInput("");
      onClose();
      return;
    }

    if (nav.type === "pollenCard") {
      activatePollenCard(nav.cardKey);
      return;
    }

    if (nav.type === "logContact") {
      if (currentView === "memberDetail" && selectedMember) {
        triggerLogContact();
        setInput("");
        onClose();
      } else {
        showMessage("Open a donor first to log a contact", "error");
        setInput("");
        onClose();
      }
      return;
    }

    // help and pollenMenu: keep the bar open so the user can pick
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2000,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Palette container */}
      <div
        style={{
          position: "fixed",
          top: "22%",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2001,
          width: "min(520px, 90vw)",
          fontFamily: FONT_FAMILY,
          animation: "quickTaskBarDrop 0.15s ease-out",
        }}
      >
        {/* Input card */}
        <div
          style={{
            background: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 14,
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            overflow: "hidden",
          }}
        >
          {/* Input row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "14px 18px",
            }}
          >
            {nav ? (
              <Compass size={ICON_SIZES.md} style={{ color: palette.accent1, flexShrink: 0 }} />
            ) : (
              <Command size={ICON_SIZES.md} style={{ color: palette.text3, flexShrink: 0 }} />
            )}
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (nav?.type === "pollenMenu") {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setPollenMenuIndex((idx) =>
                      Math.min(idx + 1, POLLEN_CARD_REGISTRY.length - 1)
                    );
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setPollenMenuIndex((idx) => Math.max(idx - 1, 0));
                    return;
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const card = POLLEN_CARD_REGISTRY[pollenMenuIndex];
                    if (card) activatePollenCard(card.key);
                    return;
                  }
                }
                if (e.key === "Enter") {
                  if (nav && nav.type !== "help" && nav.type !== "pollenMenu") {
                    handleNavigate();
                  } else if (parsed?.title) {
                    handleSubmit();
                  }
                }
              }}
              placeholder="task: follow up with Sarah next Tuesday  ·  nav: /home, /find Smith, /cr"
              style={{
                flex: 1,
                padding: "4px 0",
                fontSize: FONT_SIZES.sm,
                fontFamily: FONT_FAMILY,
                background: "transparent",
                color: palette.text1,
                border: "none",
                outline: "none",
              }}
            />
            {nav && nav.type !== "help" && nav.type !== "pollenMenu" && (
              <button
                onClick={handleNavigate}
                style={{
                  background: palette.accent1,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 14px",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 700,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  flexShrink: 0,
                }}
              >
                Go
                <ArrowRight size={ICON_SIZES.xs} />
              </button>
            )}
            {!nav && parsed?.title && (
              <button
                onClick={handleSubmit}
                style={{
                  background: palette.accent1,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 14px",
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 700,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  flexShrink: 0,
                }}
              >
                Add
                <ArrowRight size={ICON_SIZES.xs} />
              </button>
            )}
          </div>

          {/* Navigation preview (when a nav command matches) */}
          {nav && nav.type !== "help" && nav.type !== "pollenMenu" && (
            <div
              style={{
                padding: "8px 18px 12px",
                borderTop: `1px solid ${palette.cardBorder}`,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <Chip
                icon={Compass}
                label={nav.label}
                color={palette.accent1}
                palette={palette}
              />
              <span
                style={{
                  fontSize: "0.55rem",
                  color: palette.text3,
                  marginLeft: "auto",
                }}
              >
                press Enter to navigate
              </span>
            </div>
          )}

          {/* Pollen submenu — listed when /pollen is typed alone */}
          {nav && nav.type === "pollenMenu" && (
            <div
              style={{
                borderTop: `1px solid ${palette.cardBorder}`,
                maxHeight: 320,
                overflowY: "auto",
                padding: "6px 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 18px",
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: palette.text3,
                }}
              >
                <Flower2 size={ICON_SIZES.xs} />
                Pollen cards
              </div>
              {POLLEN_CARD_REGISTRY.map((card, idx) => {
                const isActive = idx === pollenMenuIndex;
                return (
                  <div
                    key={card.key}
                    onClick={() => activatePollenCard(card.key)}
                    onMouseEnter={() => setPollenMenuIndex(idx)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 18px",
                      cursor: "pointer",
                      background: isActive ? `${palette.accent1}14` : "transparent",
                      borderLeft: `2px solid ${
                        isActive ? palette.accent1 : "transparent"
                      }`,
                    }}
                  >
                    <Flower2
                      size={ICON_SIZES.xs}
                      style={{
                        color: isActive ? palette.accent1 : palette.text3,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: FONT_SIZES.xs,
                        fontWeight: 600,
                        color: palette.text1,
                        fontFamily: FONT_FAMILY,
                        flex: 1,
                      }}
                    >
                      {card.label}
                    </span>
                    <span
                      style={{
                        fontSize: "0.55rem",
                        color: palette.text3,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {card.description}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Help panel — full command list when /help or /? is typed */}
          {nav && nav.type === "help" && (
            <div
              style={{
                padding: "10px 18px 14px",
                borderTop: `1px solid ${palette.cardBorder}`,
                maxHeight: 280,
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 8,
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: palette.text3,
                }}
              >
                <HelpCircle size={ICON_SIZES.xs} />
                Navigation commands
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {NAV_COMMAND_LIST.map((row) => (
                  <div
                    key={row.action}
                    style={{
                      display: "flex",
                      gap: 12,
                      fontSize: "0.65rem",
                      fontFamily: FONT_FAMILY,
                      lineHeight: 1.4,
                    }}
                  >
                    <span
                      style={{
                        color: palette.accent1,
                        fontWeight: 600,
                        minWidth: 200,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.keys}
                    </span>
                    <span style={{ color: palette.text2 }}>{row.action}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview strip */}
          {!nav && hasExtras && (
            <div
              style={{
                padding: "8px 18px 12px",
                borderTop: `1px solid ${palette.cardBorder}`,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {/* Parsed title */}
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  color: palette.text1,
                  marginRight: 4,
                }}
              >
                {parsed.title}
              </span>

              {/* Due date chip */}
              {parsed.dueDate && (
                <Chip
                  icon={Calendar}
                  label={formatPreviewDate(parsed.dueDate)}
                  color="#3b82f6"
                  palette={palette}
                />
              )}

              {/* Priority chip (only if not normal) */}
              {parsed.priority !== "normal" && (
                <Chip
                  icon={AlertCircle}
                  label={getPriorityLabel(parsed.priority).name}
                  color={getPriorityLabel(parsed.priority).color}
                  palette={palette}
                />
              )}

              {/* Category chip (only if not general) */}
              {parsed.category !== "general" && (
                <Chip
                  icon={Tag}
                  label={getCategoryLabel(parsed.category).name}
                  color={getCategoryLabel(parsed.category).color}
                  palette={palette}
                />
              )}

              {/* Donor chip */}
              {parsed.memberName && (
                <Chip
                  icon={User}
                  label={parsed.memberName}
                  color={palette.accent1}
                  palette={palette}
                />
              )}
            </div>
          )}

          {/* Hint footer */}
          <div
            style={{
              padding: "6px 18px 10px",
              borderTop:
                hasExtras || nav ? "none" : `1px solid ${palette.cardBorder}`,
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <HintKey
              label="Enter"
              text={nav ? "navigate" : "create"}
              palette={palette}
            />
            <HintKey label="Esc" text="cancel" palette={palette} />
            <span
              style={{
                fontSize: "0.55rem",
                color: palette.text3,
                marginLeft: "auto",
              }}
            >
              Type /help for commands
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes quickTaskBarDrop {
          from { opacity: 0; transform: translateX(-50%) translateY(-12px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </>
  );
};

// ── Sub-components ──────────────────────────────────────────────

const Chip = ({ icon: Icon, label, color, palette }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "2px 8px",
      borderRadius: 6,
      fontSize: "0.6rem",
      fontWeight: 600,
      fontFamily: FONT_FAMILY,
      color: color,
      background: `${color}15`,
    }}
  >
    <Icon size={ICON_SIZES.xs} />
    {label}
  </span>
);

const HintKey = ({ label, text, palette }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: "0.55rem",
      color: palette.text3,
    }}
  >
    <kbd
      style={{
        padding: "1px 5px",
        borderRadius: 4,
        fontSize: "0.5rem",
        fontFamily: FONT_FAMILY,
        border: `1px solid ${palette.cardBorder}`,
        background: `${palette.text3}10`,
        color: palette.text3,
      }}
    >
      {label}
    </kbd>
    {text}
  </span>
);

const formatPreviewDate = (dateStr) => {
  const date = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (diffDays > 0 && diffDays <= 7) return dayNames[date.getDay()];
  return `${monthNames[date.getMonth()]} ${date.getDate()}`;
};
